import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dealerAPI } from '../../services/dealerAPI';
import ListingCreditsModal from '../../components/dealer/ListingCreditsModal';

const FUEL_TYPES     = ['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'];
const TRANSMISSIONS  = ['Manual', 'Automatic'];
const CURRENT_YEAR   = new Date().getFullYear();

export default function DealerSellCar() {
  const navigate   = useNavigate();
  const imagesRef  = useRef();

  const [credits,       setCredits]       = useState(null);  // null = loading
  const [showPurchase,  setShowPurchase]  = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [success,       setSuccess]       = useState(false);
  const [error,         setError]         = useState('');
  const [imagePreviews, setImagePreviews] = useState([]);

  const [form, setForm] = useState({
    brand: '', model: '', year: CURRENT_YEAR, km_driven: '',
    fuel_type: 'Petrol', transmission: 'Manual', color: '',
    registration_number: '', asking_price: '', description: '',
  });

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Load credit balance
  const loadCredits = () => {
    dealerAPI.getListingCreditPlans()
      .then(res => setCredits(res.data.available_credits ?? 0))
      .catch(() => setCredits(0));
  };
  useEffect(loadCredits, []);

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    setImagePreviews(files.map(f => URL.createObjectURL(f)));
  };

  const handleSubmit = async () => {
    if (credits === 0) { setShowPurchase(true); return; }
    if (!form.brand || !form.model || !form.year) {
      setError('Brand, Model and Year are required.'); return;
    }
    setError('');
    setSubmitting(true);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      const files = imagesRef.current?.files;
      if (files) Array.from(files).forEach(f => fd.append('images[]', f));

      await dealerAPI.createCarListing(fd);
      setSuccess(true);
    } catch (err) {
      const data = err.response?.data;
      if (data?.needs_purchase) { setShowPurchase(true); }
      else { setError(data?.error || data?.errors?.join(', ') || 'Failed to submit listing.'); }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 32, textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#28a745,#20c997)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, margin: '0 auto 20px' }}>✓</div>
        <h2 style={{ color: '#003566', marginBottom: 8 }}>Car Submitted!</h2>
        <p style={{ color: '#666', marginBottom: 24 }}>Our admin will review your listing and approve it. You'll be notified once it's live.</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button onClick={() => { setSuccess(false); setForm({ brand:'',model:'',year:CURRENT_YEAR,km_driven:'',fuel_type:'Petrol',transmission:'Manual',color:'',registration_number:'',asking_price:'',description:'' }); setImagePreviews([]); loadCredits(); }}
            style={{ padding: '11px 24px', background: '#fff', border: '2px solid #003566', borderRadius: 10, color: '#003566', fontWeight: 700, cursor: 'pointer' }}>
            + Add Another
          </button>
          <button onClick={() => navigate('/dealer/my-car-listings')}
            style={{ padding: '11px 24px', background: 'linear-gradient(135deg,#003566,#001d3d)', border: '2px solid #FFD60A', borderRadius: 10, color: '#FFD60A', fontWeight: 700, cursor: 'pointer' }}>
            View My Listings →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#003566', margin: '0 0 4px' }}>
          🚗 List Your Car
        </h1>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
          Submit your car for listing on CarYugo. Admin will review and publish it.
        </p>
      </div>

      {/* Credit status banner */}
      <div style={{
        background: credits === 0 ? '#fff1f1' : credits === null ? '#f3f4f6' : '#f0fdf4',
        border: `1px solid ${credits === 0 ? '#fca5a5' : credits === null ? '#e5e7eb' : '#86efac'}`,
        borderRadius: 12, padding: '14px 18px', marginBottom: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <div>
          {credits === null ? (
            <span style={{ color: '#6b7280' }}>Loading credit balance…</span>
          ) : credits === 0 ? (
            <>
              <p style={{ margin: '0 0 2px', fontWeight: 700, color: '#dc2626' }}>⚠️ No listing credits remaining</p>
              <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>Purchase credits to submit your car.</p>
            </>
          ) : (
            <>
              <p style={{ margin: '0 0 2px', fontWeight: 700, color: '#166534' }}>✅ {credits} listing credit{credits !== 1 ? 's' : ''} available</p>
              <p style={{ margin: 0, fontSize: 12, color: '#166534' }}>1 credit will be deducted after admin approval.</p>
            </>
          )}
        </div>
        <button
          onClick={() => setShowPurchase(true)}
          style={{ padding: '8px 16px', background: credits === 0 ? '#dc2626' : '#003566', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          {credits === 0 ? 'Buy Credits' : '+ More Credits'}
        </button>
      </div>

      {/* Form */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 2px 12px rgba(0,53,102,0.08)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          <Field label="Brand *">
            <input className="di" value={form.brand} onChange={e => f('brand', e.target.value)} placeholder="e.g. Maruti, Honda" />
          </Field>

          <Field label="Model *">
            <input className="di" value={form.model} onChange={e => f('model', e.target.value)} placeholder="e.g. Swift, City" />
          </Field>

          <Field label="Year *">
            <input className="di" type="number" min={2000} max={CURRENT_YEAR} value={form.year} onChange={e => f('year', e.target.value)} />
          </Field>

          <Field label="KM Driven">
            <input className="di" type="number" value={form.km_driven} onChange={e => f('km_driven', e.target.value)} placeholder="e.g. 45000" />
          </Field>

          <Field label="Fuel Type">
            <select className="di" value={form.fuel_type} onChange={e => f('fuel_type', e.target.value)}>
              {FUEL_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>

          <Field label="Transmission">
            <select className="di" value={form.transmission} onChange={e => f('transmission', e.target.value)}>
              {TRANSMISSIONS.map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>

          <Field label="Color">
            <input className="di" value={form.color} onChange={e => f('color', e.target.value)} placeholder="e.g. White, Red" />
          </Field>

          <Field label="Registration Number">
            <input className="di" value={form.registration_number} onChange={e => f('registration_number', e.target.value.toUpperCase())} placeholder="MH12AB1234" />
          </Field>

          <Field label="Your Asking Price (₹)" span>
            <input className="di" type="number" value={form.asking_price} onChange={e => f('asking_price', e.target.value)} placeholder="e.g. 500000" />
          </Field>

          <Field label="Description (optional)" span>
            <textarea className="di" rows={3} value={form.description} onChange={e => f('description', e.target.value)} placeholder="Any notes about condition, accessories, etc." style={{ resize: 'none' }} />
          </Field>

          {/* Images */}
          <Field label="Car Photos" span>
            <div
              onClick={() => imagesRef.current?.click()}
              style={{
                border: '2px dashed #d1d5db', borderRadius: 12, padding: 20,
                textAlign: 'center', cursor: 'pointer', background: '#fafafa',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#003566'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#d1d5db'}
            >
              {imagePreviews.length > 0 ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {imagePreviews.map((src, i) => (
                    <img key={i} src={src} alt="" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  ))}
                  <div style={{ alignSelf: 'center', fontSize: 12, color: '#0057b7', fontWeight: 600 }}>Click to add more</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#003566' }}>Upload Car Photos</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>JPG, PNG · Max 5MB each</div>
                </div>
              )}
            </div>
            <input ref={imagesRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImages} />
          </Field>
        </div>

        {error && (
          <div style={{ background: '#fff1f1', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginTop: 16 }}>
            ⚠️ {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting || credits === null}
          style={{
            marginTop: 24, width: '100%', padding: 14,
            background: credits === 0 ? '#dc2626' : 'linear-gradient(135deg,#003566,#001d3d)',
            border: `2px solid ${credits === 0 ? '#dc2626' : '#FFD60A'}`,
            borderRadius: 10, color: credits === 0 ? '#fff' : '#FFD60A',
            fontWeight: 800, fontSize: 15, cursor: submitting ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {submitting ? (
            <><div style={{ width: 20, height: 20, border: '3px solid rgba(255,214,10,0.4)', borderTopColor: '#FFD60A', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> Submitting…</>
          ) : credits === 0 ? (
            '🛒 Buy Credits to Submit'
          ) : (
            '🚀 Submit for Review'
          )}
        </button>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
          1 credit will be deducted only after admin approves your listing.
        </p>
      </div>

      {/* Inline CSS for inputs */}
      <style>{`
        .di {
          width: 100%;
          padding: 10px 13px;
          border: 1.5px solid #e5e7eb;
          border-radius: 9px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #0A0A0A;
          outline: none;
          background: #fafafa;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .di:focus { border-color: #003566; box-shadow: 0 0 0 3px rgba(0,53,102,0.09); background: #fff; }
        .di::placeholder { color: #b0b7c3; }
      `}</style>

      {showPurchase && (
        <ListingCreditsModal
          onClose={() => setShowPurchase(false)}
          onSuccess={() => { loadCredits(); setShowPurchase(false); }}
        />
      )}
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <div style={{ gridColumn: span ? '1 / -1' : undefined, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#003566', letterSpacing: 0.3 }}>{label}</label>
      {children}
    </div>
  );
}